'use client'

import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Plus, Minus, ShoppingBag, Trash2, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Separator } from '@/components/ui/separator'
import { ImageWithFallback } from './ImageWithFallback'
import type { CartItem } from './types'
import { formatCurrency } from '@/lib/useDefaultCurrency'
import { useLanguage } from '@/components/i18n/LanguageProvider'
import fr from '@/i18n/fr.json'
import en from '@/i18n/en.json'

type ModernCartProps = {
  items: CartItem[]
  isOpen: boolean
  onClose: () => void
  onUpdateQuantity: (id: string, quantity: number) => void
  onRemove: (id: string) => void
  onCheckout?: () => void
  checkoutLoading?: boolean
  showCheckoutForm?: boolean
  shouldShowChoice?: boolean
  resetChoiceToken?: number
  currencyCode?: string
  currencySymbol?: string | null
  checkoutFields?: {
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
    shippingAddress?: string
    billingAddress?: string
  }
  onCheckoutFieldChange?: (field: string, value: string) => void
  onGuestCheckout?: () => void
}

export function ModernCart({
  items,
  isOpen,
  onClose,
  onUpdateQuantity,
  onRemove,
  onCheckout,
  checkoutLoading,
  showCheckoutForm,
  shouldShowChoice = true,
  resetChoiceToken,
  currencyCode,
  currencySymbol,
  checkoutFields,
  onCheckoutFieldChange,
  onGuestCheckout,
}: ModernCartProps) {
  const [showChoice, setShowChoice] = useState(false)
  const [showGuestForm, setShowGuestForm] = useState(false)
  const { language, callingCode } = useLanguage()
  const copy = language === 'fr' ? fr : en

  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  const shipping = subtotal > 50 ? 0 : 5.99
  const total = subtotal + shipping
  const currency = currencyCode || items[0]?.currency || '€'

  useEffect(() => {
    if (!isOpen && !showChoice) {
      setShowGuestForm(false)
    }
  }, [isOpen, showChoice])

  useEffect(() => {
    if (resetChoiceToken !== undefined) {
      setShowChoice(false)
      setShowGuestForm(false)
    }
  }, [resetChoiceToken])

  const openCheckoutChoice = () => {
    setShowChoice(true)
    setShowGuestForm(false)
    onClose?.() // ferme le panneau panier pour ne laisser que la popup
  }

  const closeCheckoutChoice = () => {
    setShowChoice(false)
    setShowGuestForm(false)
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="flex w-full flex-col sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-primary" />
            <span>Votre Panier ({items.length})</span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center py-12 text-center">
            <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
              <ShoppingBag className="h-10 w-10 text-muted-foreground" />
            </div>
            <p className="mb-2 text-lg text-muted-foreground">{language === 'fr' ? 'Votre panier est vide' : 'Your cart is empty'}</p>
            <p className="mb-6 text-sm text-muted-foreground">{language === 'fr' ? 'Ajoutez des produits pour continuer' : 'Add products to continue'}</p>
            <Button onClick={onClose} className="bg-primary text-primary-foreground hover:bg-primary/90">
              {copy.cart.discover}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <AnimatePresence mode="popLayout">
                {items.map((item) => (
                  <motion.div key={item.id} layout initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="group mb-6 flex gap-4">
                    <div className="h-24 w-24 overflow-hidden rounded-2xl bg-muted">
                      <ImageWithFallback src={item.image} alt={item.name} className="h-full w-full object-cover" />
                    </div>
                    <div className="flex flex-1 flex-col">
                      <div className="mb-2 flex items-start justify-between">
                        <div className="min-w-0 flex-1 pr-2">
                          <h3 className="mb-1 line-clamp-2 text-sm">{item.name}</h3>
                          <p className="text-xs text-muted-foreground">{item.weight}</p>
                        </div>
                        <Button variant='ghost' size='icon' className="opacity-0 transition-opacity group-hover:opacity-100" onClick={() => onRemove(item.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 rounded-full bg-muted p-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 rounded-full hover:bg-background"
                            onClick={() => onUpdateQuantity(item.id, Math.max(1, item.quantity - 1))}
                            disabled={item.quantity <= 1}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center text-sm">{item.quantity}</span>
                          <Button variant='ghost' size='icon' className="h-7 w-7 rounded-full hover:bg-background" onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}>
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="text-primary">
                          {formatCurrency(item.price * item.quantity, { code: currency, symbol: currencySymbol })}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            <div className="space-y-4 border-t border-border px-2 pt-6">
              {subtotal < 50 && (
                <motion.div className="rounded-2xl bg-accent/50 p-4" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                  <p className="text-sm text-foreground/70">
                    Ajoutez <span className="text-primary">{(50 - subtotal).toFixed(2)}{currency}</span> pour bénéficier de la livraison gratuite
                  </p>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                    <motion.div className="h-full bg-primary" initial={{ width: 0 }} animate={{ width: `${Math.min((subtotal / 50) * 100, 100)}%` }} transition={{ duration: 0.5 }} />
                  </div>
                </motion.div>
              )}

              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>Sous-total</span>
                  <span>{formatCurrency(subtotal, { code: currency, symbol: currencySymbol })}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Livraison</span>
                  <span className={shipping === 0 ? 'text-primary' : ''}>
                    {shipping === 0 ? 'Gratuit' : formatCurrency(shipping, { code: currency, symbol: currencySymbol })}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between text-base">
                  <span>Total</span>
                  <span className="text-2xl text-primary">{formatCurrency(total, { code: currency, symbol: currencySymbol })}</span>
                </div>
              </div>

              {showCheckoutForm && (
                <div className="space-y-3 rounded-2xl border border-border/60 bg-muted/40 p-4">
                  <p className="text-sm font-semibold text-foreground">Vos informations</p>
                  <div className="grid gap-3 md:grid-cols-2">
                    <input
                      className="h-10 rounded-md border border-border bg-background px-3 text-sm"
                      placeholder="Prénom"
                      value={checkoutFields?.firstName || ''}
                      onChange={(e) => onCheckoutFieldChange?.('firstName', e.target.value)}
                    />
                    <input
                      className="h-10 rounded-md border border-border bg-background px-3 text-sm"
                      placeholder="Nom"
                      value={checkoutFields?.lastName || ''}
                      onChange={(e) => onCheckoutFieldChange?.('lastName', e.target.value)}
                    />
                    <input
                      className="h-10 rounded-md border border-border bg-background px-3 text-sm md:col-span-2"
                      placeholder={`${callingCode} ${copy.cart?.phonePlaceholder || 'Téléphone*'}`}
                      value={checkoutFields?.phone || ''}
                      onChange={(e) => onCheckoutFieldChange?.('phone', e.target.value)}
                    />
                    <input
                      className="h-10 rounded-md border border-border bg-background px-3 text-sm md:col-span-2"
                      placeholder="Adresse de livraison*"
                      value={checkoutFields?.shippingAddress || ''}
                      onChange={(e) => onCheckoutFieldChange?.('shippingAddress', e.target.value)}
                    />
                    <input
                      className="h-10 rounded-md border border-border bg-background px-3 text-sm md:col-span-2"
                      placeholder="Adresse de facturation (optionnelle)"
                      value={checkoutFields?.billingAddress || ''}
                      onChange={(e) => onCheckoutFieldChange?.('billingAddress', e.target.value)}
                    />
                  </div>
                </div>
              )}

              <Button
                size="lg"
                className="w-full rounded-full bg-primary py-6 text-primary-foreground hover:bg-primary/90"
                onClick={() => {
                  if (shouldShowChoice) {
                    openCheckoutChoice()
                  } else {
                    onCheckout?.()
                  }
                }}
                disabled={checkoutLoading || items.length === 0 || (!shouldShowChoice && !onCheckout)}
              >
                {checkoutLoading ? 'Validation…' : 'Passer la commande'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button variant='ghost' className="w-full" onClick={onClose}>
                Continuer mes achats
              </Button>
            </div>
          </>
        )}
      </SheetContent>
      <AnimatePresence>
        {showChoice && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCheckoutChoice}
          >
            <motion.div
              className="relative w-full max-w-xl rounded-2xl border border-border/60 bg-card p-6 shadow-2xl"
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <p className="text-lg font-semibold">Finaliser ma commande</p>
                  <p className="text-sm text-muted-foreground">
                    Choisissez de continuer en invité ou de vous connecter pour retrouver vos infos sauvegardées.
                  </p>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={closeCheckoutChoice}>
                  <span className="sr-only">Fermer</span>
                  x
                </Button>
              </div>

              {!showGuestForm ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-xl border border-border/70 p-4">
                    <p className="text-base font-semibold">Continuer en tant qu&apos;invité</p>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Renseignez vos coordonnées pour valider votre commande sans créer de compte.
                    </p>
                    <Button className="mt-4 w-full" onClick={() => setShowGuestForm(true)} disabled={!onGuestCheckout}>
                      Remplir mes infos
                    </Button>
                  </div>
                  <div className="rounded-xl border border-border/70 p-4">
                    <p className="text-base font-semibold">Se connecter</p>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Accédez à vos adresses enregistrées et à l’historique de vos commandes.
                    </p>
                    <Button
                      variant="outline"
                      className="mt-4 w-full"
                      onClick={() => {
                        closeCheckoutChoice()
                        onCheckout?.()
                      }}
                      disabled={!onCheckout}
                    >
                      Me connecter
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-base font-semibold">Commander en invité</p>
                    <Button variant="ghost" size="sm" onClick={() => setShowGuestForm(false)}>
                      Revenir aux choix
                    </Button>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <input
                      className="h-10 rounded-md border border-border bg-background px-3 text-sm md:col-span-2"
                      placeholder="Email (optionnel)"
                      type="email"
                      value={checkoutFields?.email || ''}
                      onChange={(e) => onCheckoutFieldChange?.('email', e.target.value)}
                    />
                    <input
                      className="h-10 rounded-md border border-border bg-background px-3 text-sm"
                      placeholder="Prénom"
                      value={checkoutFields?.firstName || ''}
                      onChange={(e) => onCheckoutFieldChange?.('firstName', e.target.value)}
                      required
                    />
                    <input
                      className="h-10 rounded-md border border-border bg-background px-3 text-sm"
                      placeholder="Nom"
                      value={checkoutFields?.lastName || ''}
                      onChange={(e) => onCheckoutFieldChange?.('lastName', e.target.value)}
                      required
                    />
                    <input
                      className="h-10 rounded-md border border-border bg-background px-3 text-sm md:col-span-2"
                      placeholder={`${callingCode} ${copy.cart?.phonePlaceholder || 'Téléphone*'}`}
                      value={checkoutFields?.phone || ''}
                      onChange={(e) => onCheckoutFieldChange?.('phone', e.target.value)}
                      required
                    />
                    <input
                      className="h-10 rounded-md border border-border bg-background px-3 text-sm md:col-span-2"
                      placeholder="Adresse de livraison*"
                      value={checkoutFields?.shippingAddress || ''}
                      onChange={(e) => onCheckoutFieldChange?.('shippingAddress', e.target.value)}
                      required
                    />
                    <input
                      className="h-10 rounded-md border border-border bg-background px-3 text-sm md:col-span-2"
                      placeholder="Adresse de facturation (optionnelle)"
                      value={checkoutFields?.billingAddress || ''}
                      onChange={(e) => onCheckoutFieldChange?.('billingAddress', e.target.value)}
                    />
                  </div>
                  <Button
                    className="w-full"
                    onClick={() => {
                      onGuestCheckout?.()
                    }}
                    disabled={
                      checkoutLoading ||
                      !onGuestCheckout ||
                      !checkoutFields?.firstName ||
                      !checkoutFields?.lastName ||
                      !checkoutFields?.phone ||
                      !checkoutFields?.shippingAddress
                    }
                  >
                    {checkoutLoading ? 'Validation…' : 'Valider ma commande'}
                  </Button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Sheet>
  )
}
